import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router';
import useScrollToTopOnPageChange from '../../../hooks/scroll/useScrollToTopOnPageChange';
import {Divider, Grid, Typography} from '@mui/material';
import LocationImageGallery from '../../../components/location/profile/LocationImageGallery';
import LocationProfileDescription from '../../../components/location/profile/LocationProfileDescription';
import LocationProfileMapPreview, { LocationProfileMapCaption } from '../../../components/location/profile/LocationProfileMapPreview';
import LocationProfileNearby from '../../../components/location/profile/LocationProfileNearby';
import LocationProfileFeatures from '../../../components/location/profile/features/LocationProfileFeatures';
import LocationProfilePriceBreakdown from '../../../components/location/profile/LocationProfilePriceBreakdown';
import LocationProfileBookSpaceForm from '../../../components/location/profile/LocationProfileBookSpaceForm';
import LocationProfileBeforeYouBook from '../../../components/location/profile/LocationProfileBeforeYouBook';
import LocationProfileSimilarYardsNearby from '../../../components/location/profile/LocationProfileSimilarYardsNearby';
import LocationProfileHeader from '../../../components/location/profile/LocationProfileHeader';
import {
  getLocationByIdWithDates,
  getLocationHasRequestedCapacityById
} from '../../../components/location/requests/location-requests';
import moment from 'moment';
import SecurSpaceContainer from '../../../components/common/SecurSpaceContainer';
import './style.css'
import {DateFormats} from '../../../components/constants/securspace-constants';
import {DAILY, MONTHLY} from "../../../util/BookingUtil";
import {DEFAULT_DAILY_BOOKING_DAYS_AHEAD, DEFAULT_NUM_SPACES} from "../../../components/constants/book-space-constants";
import {withSnackbar} from "../../../components/hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../util/NetworkErrorUtil";
import {hasEquipmentOverageRate} from "../../create-location-wizard/equipmentOverageRate";
import OverageRatePrice from "../../../components/location/profile/OverageRatePrice";
import {AppContext} from "../../../context/app-context";
import {isSupplierAccount} from "../../../util/AccountUtils";
import LocationProfileSEO, {breadcrumbTrail} from "../../../components/location/profile/LocationProfileSEO";
import LocationProfileBreadcrumbs from "../../../components/location/profile/LocationProfileBreadcrumbs";
import {styled} from "@mui/material/styles";
import {usePrerenderReady} from "../../../hooks/usePrerenderReady";
import {useFeatureFlags} from "../../../hooks/routing/useFeatureFlags";

const LpMapText = styled(Typography)(() => ({
  fontSize: '.8571428em',
  lineHeight: '166%',
  marginTop: '1.2em',
}));

const LpHeaderDivider = styled(Divider)(() => ({
  height: '0.07rem',
  color: '#e0e0e09d',
}));

const LpSectionDivider = styled(Divider)(() => ({
  height: '0.08rem',
  marginTop: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#e0e0e09d',
}));

const LpBody = styled(Grid)(() => ({
  marginTop: '1.5rem',
}));

const LpBeforeYouBookContainer = styled(Grid)(() => ({
  marginTop: '2.14rem',
}));

const LpMainBg = styled('section')(() => ({
  backgroundColor: '#fff'
}));

const LpListingInfoSection = styled(Grid)(({theme}) => ({
  rowGap: '1.07rem',
  [theme.breakpoints.between('md', 'lg')]: {
    width: '57.14rem',
  }
}));

// The section is a block, so its rowGap never applies: space the map off the gallery itself.
const LpMapPreviewItem = styled(Grid)(() => ({
  marginTop: '1.07rem',
}));

// Holds the breadcrumb's one line (0.86rem × 1.5, plus its 8px margin) while the
// yard loads, so the crumb doesn't push the page down when it appears.
const LpBreadcrumbSpace = styled('div')(() => ({
  height: '1.29rem',
  marginBottom: '8px',
}));

const LpDivider = styled(Grid)(() => ({
  borderBottom: `1px solid #e0e0e09d`,
  paddingBottom: '0.1em'
}));

const LocationProfile = ({ snackbarShowMessage }) => {
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  // A Nearby place to show on the map; a new object each click so the same place can be shown again.
  const [mapFocus, setMapFocus] = useState(undefined);
  // The route reuses this page for the next yard; a pick on the last one must not move its map.
  useEffect(() => setMapFocus(undefined), [locationId]);
  // True once the location fetch has settled (success or failure), so the
  // prerender snapshot waits for the real title/JSON-LD but is never held
  // hostage by a 404.
  const [locationFetched, setLocationFetched] = useState(false);
  // The API says the yard doesn't exist (404, or its 422 "Location not found"): keep the
  // page out of search. A failed request doesn't count (securspace-ui#3107).
  const [locationMissing, setLocationMissing] = useState(false);
  const [capacityRequest, setCapacityRequest] = useState({
    durationType: DAILY,
    startDate: moment().format(DateFormats.DAY),
    endDate: moment().add(DEFAULT_DAILY_BOOKING_DAYS_AHEAD, 'days').format(DateFormats.DAY),
    numberOfSpaces: DEFAULT_NUM_SPACES,
  });
  const [locationHasRequestedCapacity, setLocationHasRequestedCapacity] = useState(null);
  const [firstUnavailableDate, setFirstUnavailableDate] = useState(null);
  const [minimumSpacesUpdated, setMinimumSpacesUpdated] = useState(false);
  const [isBrokeredBooking, setIsBrokeredBooking] = useState(false);
  const initialCapacityLoaded = useRef(false);

  const { user }  = useContext(AppContext);
  const { supplierCapacitySchedule, locationFeatureDetails } = useFeatureFlags();

  useScrollToTopOnPageChange();
  usePrerenderReady(locationFetched);

  useEffect(() => {
    // A location change while a fetch is in flight must not let the OLD
    // response set state (or flag the page ready) for the NEW id.
    let stale = false;
    if (locationId) {
      setLoading(true)
      setLocationFetched(false);
      setLocationMissing(false);
      // The route can reuse this component for another id: drop the previous yard so a
      // failed or missing one never shows it under the new URL.
      setLocation(null);

      const startDate = moment().format(DateFormats.DAY);
      const endDate = moment().add(1, 'days').format(DateFormats.DAY);

      // Check if URL params indicate a monthly deep-link. When they do, the daily
      // capacity response would be stale — skip seeding it and let the form's
      // monthly capacity check provide the correct value.
      const urlShowMonth = new URLSearchParams(window.location.search).get('showMonth');
      const isMonthlyDeepLink = urlShowMonth === 'true';

      getLocationByIdWithDates(locationId, startDate, endDate).then((response) => {
        if (stale) return;
        setLocation(response.body);
        setMinimumSpacesUpdated(true);
        if (!isMonthlyDeepLink) {
          setLocationHasRequestedCapacity(response.body.hasInstantApproval);
        } else {
          // Pre-arm the guard so the capacity-check effect does NOT skip its
          // first run; the daily value would be stale for a monthly deep-link.
          initialCapacityLoaded.current = true;
        }
      }).catch((error) => {
        if (stale) return;
        if (error?.status === 404 || error?.status === 422) setLocationMissing(true);
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
      }).finally(() => {
        if (stale) return;
        setLoading(false);
        setLocationFetched(true);
      });
    }
    return () => { stale = true; };
  }, [locationId, snackbarShowMessage]);

  useEffect(() => {
    let ignore = false;
    const {numberOfSpaces, durationType} = capacityRequest;

    // These conditions are to limit the number of unnecessary calls to this endpoint
    if(minimumSpacesUpdated) {
      // Skip the first run after initial load — the API value is already set
      if (!initialCapacityLoaded.current) {
        initialCapacityLoaded.current = true;
        // The location response carries instant approval but not scheduled capacity.
        if (!supplierCapacitySchedule) return;
      }
      const meetsMinimumSpaces = isBrokeredBooking || numberOfSpaces >= location?.minNumberOfSpaces;
      // Monthly bookings always go through Request Space, so they're only checked for scheduled capacity.
      if (meetsMinimumSpaces && (durationType !== MONTHLY || supplierCapacitySchedule)) {
        // Don't keep blocking on a result for different dates while this check runs or if it fails.
        setFirstUnavailableDate(null);
        getLocationHasRequestedCapacityById(
          locationId,
          numberOfSpaces,
          capacityRequest.startDate,
          capacityRequest.endDate,
          durationType
        ).then((response) => {
          if (!ignore) {
            const {hasRequestedCapacity, hasAvailableCapacity, firstUnavailableDate} = response.body;
            setLocationHasRequestedCapacity(durationType !== MONTHLY && hasRequestedCapacity);
            setFirstUnavailableDate(supplierCapacitySchedule && hasAvailableCapacity === false ? firstUnavailableDate ?? null : null);
          }
        }).catch(error => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
        });
      } else {
        setLocationHasRequestedCapacity(false);
        setFirstUnavailableDate(null);
      }
    }
    return () => {
      ignore = true;
    }
  }, [locationId, capacityRequest, minimumSpacesUpdated, isBrokeredBooking, snackbarShowMessage, location, supplierCapacitySchedule]);

  const handleCapacityRequestChange = useCallback(function handleCapacityRequestChange(value) {
    setCapacityRequest(capacityRequest => ({
      ...capacityRequest,
      ...value,
    }));
  }, []);

  const locationGallery = useMemo(() => {
    if (!location) {
      return [];
    }
    return location.gallery ? [...new Set(location.gallery)] : [];
  }, [location]);

  // Per-equipment rates only replace the location-level rate when at least one is set.
  const showEquipmentOverageRates = !!location?.chargeOverages
    && (location?.equipmentTypesAll ?? []).some(eq => hasEquipmentOverageRate(eq.overageRate));

  return (
    <>
      <LocationProfileSEO location={location} locationId={locationId} notFound={locationMissing} />
      <LpMainBg>
        <SecurSpaceContainer >
          <Grid container direction={'column'}>
            {location ? <LocationProfileBreadcrumbs trail={breadcrumbTrail(location, locationId)} /> : <LpBreadcrumbSpace />}
            <LpDivider item container xs>
              <LocationProfileHeader
                locationId={locationId}
                locationName={location?.locationName}
                city={location?.city}
                state={location?.state}
                hasRequestedCapacity={locationHasRequestedCapacity}
                atCapacity={!!firstUnavailableDate}
              />
              {/* Under the title, not below the gallery, so buyers read it before booking. */}
              <LocationProfileDescription
                loading={loading}
                desc={location?.locationDescription}
                supplierProfileUrl={location?.supplierProfileUrl}
                supplierName={location?.companyName}
              />
            </LpDivider>
            <LpHeaderDivider variant='fullWidth' />
            <LpBody container spacing={4}>
              <LpListingInfoSection item xs={12} md>
                <Grid item>
                  <LocationImageGallery locationId={locationId} listingImage={location?.listingImageFileName} gallery={locationGallery} />
                </Grid>
                <LpMapPreviewItem item>
                  <LocationProfileMapPreview loading={loading} lat={location?.addressLatitude} lng={location?.addressLongitude} driveTimes={location?.driveTimes} focus={mapFocus} />
                </LpMapPreviewItem>
                <Grid item>
                  <LpMapText>
                    <LocationProfileMapCaption lat={location?.addressLatitude} lng={location?.addressLongitude} />
                  </LpMapText>
                </Grid>
                <Grid item>
                  <LocationProfileNearby driveTimes={location?.driveTimes} onShowOnMap={({ name, lat, lng }) => setMapFocus({ name, lat, lng })} />
                </Grid>
                <LpSectionDivider variant='fullWidth' />
                <Grid item>
                  <LocationProfileFeatures
                    loading={loading}
                    features={location?.features ? location.features : []}
                    featureDetails={location?.featureDetails}
                    showDetails={locationFeatureDetails}
                  />
                </Grid>
                <LpSectionDivider variant='fullWidth' />
                <Grid item>
                  <LocationProfilePriceBreakdown
                    equipmentTypeArray={location?.equipmentTypesAll ? location.equipmentTypesAll : []}
                    defaultPricePerDay={location?.pricePerDay}
                    defaultPricePerMonth={location?.pricePerMonth}
                    defaultOverageRate={location?.overageRate}
                    showOverageColumn={showEquipmentOverageRates}
                    loading={loading}
                  />
                </Grid>
                {location?.chargeOverages && !showEquipmentOverageRates && <LpSectionDivider variant='fullWidth' />}
                {
                  location?.chargeOverages && !showEquipmentOverageRates && <Grid item>
                    <OverageRatePrice overageRateInCents={location?.overageRate}/>
                  </Grid>
                }
              </LpListingInfoSection>
              {
                !isSupplierAccount(user) && <Grid item xs={12} md={4}>
                  <Grid container direction={'column'}>
                    <Grid item>
                      <LocationProfileBookSpaceForm
                        location={location}
                        capacityRequest={capacityRequest}
                        onCapacityRequestChange={handleCapacityRequestChange}
                        instantApproval={locationHasRequestedCapacity}
                        unavailableDate={firstUnavailableDate}
                        isBrokeredBooking={isBrokeredBooking}
                        setIsBrokeredBooking={setIsBrokeredBooking}
                        loading={loading}
                      />
                    </Grid>
                    <LpBeforeYouBookContainer item>
                      <LocationProfileBeforeYouBook rentalAgreementFileName={location?.supplierLegalAgreementFileName}/>
                    </LpBeforeYouBookContainer>
                  </Grid>
              </Grid>
              }
            </LpBody>
          </Grid>
        </SecurSpaceContainer>
      </LpMainBg>
      {
        location && <SecurSpaceContainer>
          <Grid container item xs>
            <LocationProfileSimilarYardsNearby location={location} />
          </Grid>
        </SecurSpaceContainer>
      }
    </>
  )
}

export default withSnackbar(LocationProfile);
